import React from 'react'
import { UploadElement } from 'is24-corecss'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { IconS24Bin24, IconS24DocumentPlus24 } from '@is24/cosma-ui-icons'
import { AiDataExtractionUploadError } from './AiDataExtractionUploadError'
import styles from './AiEmptyUploadDocumentSection.module.css'

export type AiUploadedDocument = {
  id: string
  name: string
  file: File
  previewUrl?: string
  uploadedAt: Date
}

interface AiUploadDocumentSectionProps {
  documentsSectionErrorText?: string
  energyCertificateErrorText?: string
  floorPlanDocument: AiUploadedDocument | null
  floorPlanErrorText?: string
  energyCertificateDocument: AiUploadedDocument | null
  onFloorPlanAddClick?: () => void
  onFloorPlanSelect: (files: FileList | null) => void
  onEnergyCertificateAddClick?: () => void
  onEnergyCertificateSelect: (files: FileList | null) => void
  onFloorPlanRemove: () => void
  onEnergyCertificateRemove: () => void
}

export const AiUploadDocumentSection = ({
  documentsSectionErrorText,
  energyCertificateErrorText,
  floorPlanDocument,
  floorPlanErrorText,
  energyCertificateDocument,
  onFloorPlanAddClick,
  onFloorPlanSelect,
  onEnergyCertificateAddClick,
  onEnergyCertificateSelect,
  onFloorPlanRemove,
  onEnergyCertificateRemove,
}: AiUploadDocumentSectionProps) => {
  const { t } = useTranslation()
  const documentTiles = [
    {
      accept: 'image/jpeg,image/gif,image/png,application/pdf',
      document: floorPlanDocument,
      emptyTitle: t('wizard.uploadAiBanner.modal.documents.floorPlan.title'),
      errorText: floorPlanErrorText,
      onAddClick: onFloorPlanAddClick,
      onFileSelect: onFloorPlanSelect,
      onRemove: onFloorPlanRemove,
      supportText: t('wizard.uploadAiBanner.modal.documents.floorPlan.supportText'),
      testId: 'ai-upload-floor-plan-tile',
    },
    {
      accept: 'application/pdf',
      document: energyCertificateDocument,
      emptyTitle: t('wizard.uploadAiBanner.modal.documents.energyCertificate.title'),
      errorText: energyCertificateErrorText,
      onAddClick: onEnergyCertificateAddClick,
      onFileSelect: onEnergyCertificateSelect,
      onRemove: onEnergyCertificateRemove,
      supportText: t('wizard.uploadAiBanner.modal.documents.energyCertificate.supportText'),
      testId: 'ai-upload-energy-certificate-tile',
    },
  ]

  return (
    <section>
      <h3 className='font-body-medium-bold margin-bottom-s'>
        {t('wizard.uploadAiBanner.modal.documents.sectionTitle')}
      </h3>
      <div className={`grid one-whole`}>
        {documentTiles.map((tile, index) => (
          <div
            key={tile.testId}
            data-testid={tile.testId}
            className={`grid-item${index === 0 ? ' margin-bottom-s' : ''}`}
          >
            <DocumentUploadTile
              accept={tile.accept}
              document={tile.document}
              emptyTitle={tile.emptyTitle}
              errorText={tile.errorText}
              onAddClick={tile.onAddClick}
              onFileSelect={tile.onFileSelect}
              onRemove={tile.onRemove}
              supportText={tile.supportText}
            />
          </div>
        ))}
      </div>
      {documentsSectionErrorText ? (
        <AiDataExtractionUploadError
          className='margin-top-s'
          data-testid='ai-upload-documents-max-size-error'
          message={documentsSectionErrorText}
          role='alert'
        />
      ) : null}
    </section>
  )
}

type DocumentUploadTileProps = {
  accept: string
  document: AiUploadedDocument | null
  emptyTitle: string
  errorText?: string
  onAddClick?: () => void
  onFileSelect: (files: FileList | null) => void
  onRemove: () => void
  supportText: string
}

const DocumentUploadTile = ({
  accept,
  document,
  emptyTitle,
  errorText,
  onAddClick,
  onFileSelect,
  onRemove,
  supportText,
}: DocumentUploadTileProps) => {
  const { t } = useTranslation()
  const uploadError = errorText ? <AiDataExtractionUploadError message={errorText} /> : null

  if (!document) {
    return (
      <UploadElement.Root
        onClick={onAddClick}
        onFileSelect={onFileSelect}
        status={errorText ? 'error' : 'empty'}
        accept={accept}
      >
        <UploadElement.Dropzone>
          <UploadElement.Icon>
            <IconS24DocumentPlus24 />
          </UploadElement.Icon>
          <UploadElement.Content>
            <UploadElement.Title>{emptyTitle}</UploadElement.Title>
            <UploadElement.Description>{supportText}</UploadElement.Description>
          </UploadElement.Content>
        </UploadElement.Dropzone>
        <UploadElement.Error>{uploadError}</UploadElement.Error>
      </UploadElement.Root>
    )
  }

  return (
    <div className={styles.uploadedDocumentTile}>
      <UploadElement.Root
        onClick={onAddClick}
        onFileSelect={onFileSelect}
        status={errorText ? 'error' : 'uploaded'}
        accept={accept}
      >
        <UploadElement.Dropzone>
          {document.previewUrl ? (
            <UploadElement.Icon alt={document.name} src={document.previewUrl} imageSize={80} />
          ) : (
            <UploadElement.Icon imageSize={80} />
          )}
          <UploadElement.Content>
            <UploadElement.Title>{document.name}</UploadElement.Title>
            <div>{getUploadedSubline(t, document.uploadedAt)}</div>
          </UploadElement.Content>
        </UploadElement.Dropzone>
        <UploadElement.Error>{uploadError}</UploadElement.Error>
      </UploadElement.Root>
      <button aria-label='Delete file' className={styles.uploadedDocumentDeleteButton} type='button' onClick={onRemove}>
        <IconS24Bin24 />
      </button>
    </div>
  )
}

const getUploadedSubline = (t: TFunction, uploadedAt: Date) => {
  const day = String(uploadedAt.getDate()).padStart(2, '0')
  const month = String(uploadedAt.getMonth() + 1).padStart(2, '0')
  const year = String(uploadedAt.getFullYear()).slice(-2)

  return t('wizard.uploadAiBanner.modal.documents.uploadedAt', {
    date: `${day}.${month}.${year}`,
  })
}
